
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, MessageSquare, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { dispatchSubscriptionEndedEvent } from "@/utils/subscriptionBookingUtils";

interface ActiveSubscription {
  id: string;
  application_id?: string;
  mentor_id: string;
  mentee_id: string;
  subscription_start_date: string;
  subscription_end_date: string | null;
  is_subscription_active: boolean;
  stripe_subscription_cancelled: boolean;
  weekly_call_allowance: number;
  mentor_name: string;
  mentee_name: string;
  mentor_avatar: string;
  mentee_avatar: string;
  course_title: string;
  course_type: string;
  subscription_type: string;
  stripe_subscription_id: string | null;
}

interface ActiveSubscriptionsSectionProps {
  userId: string;
  userType: 'mentor' | 'mentee';
}

const ActiveSubscriptionsSection = ({ userId, userType }: ActiveSubscriptionsSectionProps) => {
  const [subscriptions, setSubscriptions] = useState<ActiveSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (userId) {
      fetchActiveSubscriptions();
    }
  }, [userId, userType]);

  const fetchActiveSubscriptions = async () => {
    try {
      setLoading(true);
      
      if (!userId) {
        console.log("DEBUG: No userId provided");
        setSubscriptions([]);
        setLoading(false);
        return;
      }
      
      console.log("DEBUG: Fetching active subscriptions for:", { userId, userType });
      
      // First, update any subscriptions that should be completed due to end date
      await updateEndedSubscriptions();
      
      // Query applications with active subscriptions and join with course data
      const { data: applicationsData, error } = await supabase
        .from('applications')
        .select(`
          id,
          application_id,
          mentor_id,
          user_id,
          subscription_start_date,
          subscription_end_date,
          is_subscription_active,
          stripe_subscription_cancelled,
          weekly_call_allowance,
          course_id,
          stripe_subscription_id,
          courses!inner (
            id,
            title,
            course_type,
            subscription_type
          )
        `)
        .eq('is_subscription_active', true)
        .eq(userType === 'mentor' ? 'mentor_id' : 'user_id', userId);

      if (error) {
        console.error("DEBUG: Error fetching active subscriptions:", error);
        setSubscriptions([]);
        return;
      }

      console.log("DEBUG: Found applications data:", applicationsData);

      if (!applicationsData || applicationsData.length === 0) {
        console.log("DEBUG: No active subscriptions found");
        setSubscriptions([]);
        return;
      }

      // Filter only subscription type courses AND ensure they haven't ended
      const now = new Date();
      const activeSubscriptionApplications = applicationsData.filter(app => {
        const isSubscriptionType = app.courses && app.courses.course_type === 'subscription';
        const hasNotEnded = !app.subscription_end_date || new Date(app.subscription_end_date) > now;
        
        console.log(`DEBUG: Checking subscription ${app.id}:`, {
          isSubscriptionType,
          hasNotEnded,
          endDate: app.subscription_end_date,
          now: now.toISOString()
        });
        
        return isSubscriptionType && hasNotEnded;
      });

      console.log("DEBUG: Filtered active subscription applications:", activeSubscriptionApplications);

      if (activeSubscriptionApplications.length === 0) {
        console.log("DEBUG: No active subscription type courses found");
        setSubscriptions([]);
        return;
      }

      // Get the profiles for mentors and mentees
      const transformedSubscriptions: ActiveSubscription[] = [];
      
      for (const app of activeSubscriptionApplications) {
        try {
          // Get mentor profile
          const { data: mentorProfile, error: mentorError } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', app.mentor_id)
            .single();

          // Get mentee profile  
          const { data: menteeProfile, error: menteeError } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', app.user_id)
            .single();

          console.log("DEBUG: Profile data:", { mentorProfile, menteeProfile, mentorError, menteeError });

          // Only add if we have the required data
          if (!mentorError && !menteeError && mentorProfile && menteeProfile && app.courses) {
            transformedSubscriptions.push({
              id: app.id,
              application_id: app.application_id,
              mentor_id: app.mentor_id,
              mentee_id: app.user_id,
              subscription_start_date: app.subscription_start_date,
              subscription_end_date: app.subscription_end_date,
              is_subscription_active: app.is_subscription_active,
              stripe_subscription_cancelled: app.stripe_subscription_cancelled || false,
              weekly_call_allowance: app.weekly_call_allowance || 0,
              mentor_name: `${mentorProfile.first_name || ''} ${mentorProfile.last_name || ''}`.trim(),
              mentee_name: `${menteeProfile.first_name || ''} ${menteeProfile.last_name || ''}`.trim(),
              mentor_avatar: mentorProfile.avatar_url || '',
              mentee_avatar: menteeProfile.avatar_url || '',
              course_title: app.courses.title || 'Subscription',
              course_type: app.courses.course_type || 'subscription',
              subscription_type: app.courses.subscription_type || 'weekly',
              stripe_subscription_id: app.stripe_subscription_id
            });
          } else {
            console.log("DEBUG: Missing profile data for application:", app.id, { mentorError, menteeError });
          }
        } catch (profileError) {
          console.error("DEBUG: Error fetching profile data:", profileError);
          continue;
        }
      }

      console.log("DEBUG: Final transformed subscriptions:", transformedSubscriptions);
      setSubscriptions(transformedSubscriptions);
    } catch (error) {
      console.error("DEBUG: Exception fetching active subscriptions:", error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const updateEndedSubscriptions = async () => {
    try {
      console.log('DEBUG: Calling update_ended_subscriptions function from ActiveSubscriptionsSection');
      const { error } = await supabase.rpc('update_ended_subscriptions');
      if (error) {
        console.error('Error updating ended subscriptions:', error);
      } else {
        console.log('DEBUG: Successfully updated ended subscriptions');
      }
    } catch (error) {
      console.error('Error calling update_ended_subscriptions function:', error);
    }
  };

  const handleEndSubscription = async (subscriptionId: string) => {
    try {
      setCancellingSubscription(subscriptionId);
      
      // Get subscription details before ending it
      const subscriptionToEnd = subscriptions.find(sub => sub.id === subscriptionId);
      
      if (!subscriptionToEnd) {
        toast({
          title: "Error",
          description: "Subscription not found. Please try again.",
          variant: "destructive"
        });
        return;
      }

      console.log('DEBUG: Attempting to cancel subscription:', {
        applicationId: subscriptionId,
        stripeSubscriptionId: subscriptionToEnd.stripe_subscription_id
      });

      // Call the cancel-stripe-subscription edge function
      const { data, error } = await supabase.functions.invoke('cancel-stripe-subscription', {
        body: {
          applicationId: subscriptionId,
          stripeSubscriptionId: subscriptionToEnd.stripe_subscription_id
        }
      });

      if (error) {
        console.error("Error calling cancel-stripe-subscription function:", error);
        toast({
          title: "Error",
          description: "Failed to cancel subscription. Please try again.",
          variant: "destructive"
        });
        return;
      }

      if (!data?.success) {
        console.error("Subscription cancellation failed:", data?.error);
        toast({
          title: "Error",
          description: data?.error || "Failed to cancel subscription. Please try again.",
          variant: "destructive"
        });
        return;
      }

      console.log('DEBUG: Subscription cancelled successfully:', data);

      // Show success message with clarity about retained access
      toast({
        title: "Subscription Cancelled",
        description: data.message || "Your subscription has been cancelled. You will retain access until your billing period ends.",
        variant: "default"
      });

      // Refresh the subscriptions list to show the new status
      fetchActiveSubscriptions();
    } catch (error) {
      console.error("Exception ending subscription:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while cancelling the subscription.",
        variant: "destructive"
      });
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleMessageClick = async (subscription: ActiveSubscription) => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in to send messages.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Determine the target user ID based on the current user type
      const targetUserId = userType === 'mentor' ? subscription.mentee_id : subscription.mentor_id;
      
      // Check if conversation already exists
      const { data: existingConversation, error: conversationError } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(mentor_id.eq.${user.id},mentee_id.eq.${targetUserId}),and(mentor_id.eq.${targetUserId},mentee_id.eq.${user.id})`)
        .maybeSingle();

      if (conversationError) {
        console.error('Error checking for existing conversation:', conversationError);
        toast({
          title: "Error",
          description: "Failed to check for existing conversation.",
          variant: "destructive"
        });
        return;
      }

      if (existingConversation) {
        // Navigate to existing conversation
        navigate(`/messages/${existingConversation.id}`);
        return;
      }

      // Create new conversation
      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .insert({
          mentor_id: userType === 'mentor' ? user.id : targetUserId,
          mentee_id: userType === 'mentee' ? user.id : targetUserId
        })
        .select('id')
        .single();

      if (createError) {
        console.error('Error creating conversation:', createError);
        toast({
          title: "Error",
          description: "Failed to create conversation.",
          variant: "destructive"
        });
        return;
      }

      // Navigate to new conversation
      navigate(`/messages/${newConversation.id}`);
    } catch (error) {
      console.error('Error handling message click:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            My Active Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-gray-200 rounded" />
            <div className="h-20 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          My Active Subscriptions
        </CardTitle>
      </CardHeader>
      <CardContent>
        {subscriptions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Settings className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p>No active subscriptions</p>
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions.map((subscription) => {
              const otherUser = userType === 'mentor' 
                ? { name: subscription.mentee_name, avatar: subscription.mentee_avatar }
                : { name: subscription.mentor_name, avatar: subscription.mentor_avatar };

              const isCancelling = cancellingSubscription === subscription.id;
              const isCancelled = subscription.stripe_subscription_cancelled;

              return (
                <div key={subscription.id} className="border rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Mobile: Stack avatar and info vertically */}
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Avatar className="h-12 w-12 flex-shrink-0">
                        <AvatarImage src={otherUser.avatar} alt={otherUser.name} />
                        <AvatarFallback>
                          {otherUser.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-sm sm:text-base truncate">{otherUser.name}</h4>
                        <p className="text-sm text-gray-600 mb-1">{subscription.course_title}</p>
                        {subscription.application_id && (
                          <p className="text-xs text-muted-foreground mb-2">App ID: {subscription.application_id}</p>
                        )}
                        
                        {/* Date info with better mobile layout */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            Started {new Date(subscription.subscription_start_date).toLocaleDateString()}
                          </span>
                          {subscription.subscription_end_date && (
                            <span className="flex items-center gap-1 sm:ml-2">
                              <span className="hidden sm:inline text-gray-400">•</span>
                              Access until {new Date(subscription.subscription_end_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        {subscription.weekly_call_allowance > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {subscription.weekly_call_allowance} calls per {subscription.subscription_type === 'monthly' ? 'month' : 'week'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status badge - always visible on mobile */}
                    <div className="flex flex-col gap-3 sm:items-end">
                      <div className="flex justify-start sm:justify-end">
                        {isCancelled ? (
                          <Badge variant="outline" className="text-orange-600 border-orange-200 text-xs w-fit">
                            Cancelled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-200 text-xs w-fit">
                            Active
                          </Badge>
                        )}
                      </div>
                      
                      {/* Action buttons - stack on mobile */}
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMessageClick(subscription)}
                          className="flex items-center justify-center gap-1 min-h-[44px] text-xs sm:text-sm"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span className="sm:hidden">Message</span>
                          <span className="hidden sm:inline">{userType === 'mentor' ? 'Message Mentee' : 'Message Mentor'}</span>
                        </Button>
                        
                        {!isCancelled && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                disabled={isCancelling}
                                className="min-h-[44px] text-xs sm:text-sm"
                              >
                                {isCancelling ? "Cancelling..." : "Cancel"}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to cancel this subscription? This will prevent future payments, but you will retain access to messaging and booking features until your current billing period ends on {subscription.subscription_end_date ? new Date(subscription.subscription_end_date).toLocaleDateString() : 'the end date'}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleEndSubscription(subscription.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                  disabled={isCancelling}
                                >
                                  {isCancelling ? "Cancelling..." : "Cancel Subscription"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveSubscriptionsSection;
